import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { updateUser } from '@/actions/userActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useSignupPageUpdate } from '@/services/onboardingService'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useAnalyticsView } from '@/features/amplitude'
import { SIGNUP_PATHS } from '../helpers'
import {
  ReferralStatus,
  updateUserReferralStatusIfExists,
} from '@/features/user/referrals/slice'
import { TypographyStyles } from '@/styles'
import { Accordion, Card, CardColors, Image, Link, List, Text } from '@/ui'
import { SignupPageLayout } from '../SignupPageLayout'
import { SignupFooter } from '@/features/user/signup/SignupFooter'
import { KeepInTouchCard } from '@/features/user/signup/KeepInTouchCard'
import { DQ_REASONS } from '@/reducers/auth/userReducer'
import { RECOMMENDED_BUSINESS_BANKS } from '@/features/banking/banking.constants'

export const DisqualifiedSignup = () => {
  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()
  const [searchParams] = useSearchParams()
  const currentUser = useReselector(getCurrentUser)
  const { email } = currentUser || {}
  useSignupPageUpdate(SIGNUP_PATHS.disqualified)

  useEffect(() => {
    pageView('sign up disqualified')
  }, [pageView])

  const dqReasons = useMemo(() => {
    const reasons = []

    if (
      searchParams.get('ccorp') === 'true' ||
      searchParams.get('scorpEntireYear') === 'true'
    ) {
      reasons.push(DQ_REASONS.CCORP)
    }
    if (searchParams.get('hasInternationalTaxes') === 'true') {
      reasons.push(DQ_REASONS.HAS_INTERNATIONAL_TAXES)
    }
    if (searchParams.get('multipleOwners') === 'true') {
      reasons.push(DQ_REASONS.HAS_MULTIPLE_OWNERS)
    }
    if (searchParams.get('sharedAccounts') === 'true') {
      reasons.push(DQ_REASONS.HAS_SHARED_ACCOUNTS)
    }
    if (searchParams.get('hasPhysicalInventory') === 'true') {
      reasons.push(DQ_REASONS.HAS_PHYSICAL_INVENTORY)
    }
    // There is a separate path for this case but it needs to be handled on this screen if bundled with other reasons
    if (searchParams.get('tooManyEmployees') === 'true') {
      reasons.push(DQ_REASONS.TOO_MANY_EMPLOYEES)
    }
    return reasons
  }, [searchParams])

  useEffect(() => {
    if (currentUser?.id) {
      updateUser(currentUser.id, {
        disqualifiedReasons: dqReasons,
        disqualifiedAt: new Date().toISOString(),
      })(dispatch)
    }

    if (email) {
      updateUserReferralStatusIfExists({
        recipientEmail: email,
        status: ReferralStatus.ineligible,
      })(dispatch)
    }
  }, [email, dispatch, currentUser?.id, dqReasons])

  const descriptionText = useMemo(() => {
    if (dqReasons.length > 1) {
      return (
        <>
          <Text as={TypographyStyles.BODY_SM}>
            Based on your answers, we’re not able to support your practice right
            now because it involves:
          </Text>
          <List className="list-disc ml-4">
            {dqReasons.map((value) => (
              <List.Item key={value}>
                {(() => {
                  switch (value) {
                    case DQ_REASONS.CCORP:
                      return 'C Corporation taxes'
                    case DQ_REASONS.HAS_INTERNATIONAL_TAXES:
                      return 'International Taxes'
                    case DQ_REASONS.HAS_MULTIPLE_OWNERS:
                      return 'Multiple practice owners'
                    case DQ_REASONS.HAS_SHARED_ACCOUNTS:
                      return 'Combined personal and business banking'
                    case DQ_REASONS.HAS_PHYSICAL_INVENTORY:
                      return 'Physical inventory and sales tax requirements'
                    case DQ_REASONS.TOO_MANY_EMPLOYEES:
                      return '10 or more employees and contractors'
                    default:
                      return value satisfies never
                  }
                })()}
              </List.Item>
            ))}
          </List>
        </>
      )
    } else if (dqReasons.includes(DQ_REASONS.HAS_SHARED_ACCOUNTS)) {
      return (
        <Text as={TypographyStyles.BODY_SM}>
          Based on your answers, we’re not able to support your practice right
          now because you’re using a{' '}
          <b>personal bank account instead of a dedicated business account.</b>
          <br />
          <br />
          If you’re open to separating your finances, you can open a business
          bank account and come back to continue onboarding.
        </Text>
      )
    } else if (dqReasons.includes(DQ_REASONS.HAS_INTERNATIONAL_TAXES)) {
      return (
        <Text as={TypographyStyles.BODY_SM}>
          Based on your answers, we’re not able to support your practice right
          now because it involves <b>international tax needs.</b>
        </Text>
      )
    } else if (dqReasons.includes(DQ_REASONS.HAS_PHYSICAL_INVENTORY)) {
      return (
        <Text as={TypographyStyles.BODY_SM}>
          Based on your answers, we’re not able to support your practice right
          now because it involves{' '}
          <b>physical inventory and sales tax requirements.</b>
        </Text>
      )
    } else if (dqReasons.includes(DQ_REASONS.HAS_MULTIPLE_OWNERS)) {
      return (
        <Text as={TypographyStyles.BODY_SM}>
          Based on your answers, we’re not able to support your practice right
          now because it has <b>multiple practice owners.</b>
        </Text>
      )
    } else if (dqReasons.includes(DQ_REASONS.CCORP)) {
      return (
        <Text as={TypographyStyles.BODY_SM}>
          Based on your answers, we’re not able to support your practice right
          now because it involves <b>C Corporation tax requirements</b>.
        </Text>
      )
    }

    return null
  }, [dqReasons])

  const learnMoreAccInfo = useMemo(() => {
    const info = []

    if (dqReasons.includes(DQ_REASONS.HAS_SHARED_ACCOUNTS)) {
      info.push(
        {
          title: 'Why do I need a dedicated business account?',
          content: (
            <Text key="ccorp" as={TypographyStyles.BODY_SM}>
              To support your bookkeeping and taxes, we ask that business and
              personal finances be kept separate. Using a dedicated business
              account allows us to clearly organize transactions and keep your
              books up to date, which makes everything smoother at tax time.
            </Text>
          ),
        },
        {
          title: 'Can you recommend a business bank?',
          content: (
            <>
              <Text>Many providers start with:</Text>
              <List className="list-disc ml-4">
                {RECOMMENDED_BUSINESS_BANKS.map((bank) => (
                  <List.Item key={bank}>{bank}</List.Item>
                ))}
              </List>
              <Card color={CardColors.EMPHASIS}>
                <Text>
                  BlueVine Business Checking customers can earn a $500 signup
                  bonus with qualifying activities.{' '}
                  <Link
                    newPage
                    href="https://www.bluevine.com/partner/heard-checking"
                  >
                    Go →
                  </Link>
                </Text>
              </Card>
            </>
          ),
        }
      )
    }

    if (dqReasons.includes(DQ_REASONS.HAS_INTERNATIONAL_TAXES)) {
      info.push({
        title: 'Why can’t you support practices with international taxes?',
        content: (
          <Text key="international-taxes" as={TypographyStyles.BODY_SM}>
            Heard is currently unable to support practices with international
            tax requirements. We are working to expand our services to include
            international practices in the future.
          </Text>
        ),
      })
    }

    if (dqReasons.includes(DQ_REASONS.CCORP)) {
      info.push({
        title: 'Why can’t you support C Corporations?',
        content: (
          <Text key="ccorp" as={TypographyStyles.BODY_SM}>
            At the moment, Heard supports C Corporations (including Professional
            Corporations) only if they are taxed as S Corporations for the full
            tax year. We’re not currently able to support practices filing as C
            Corporations.
          </Text>
        ),
      })
    }

    if (dqReasons.includes(DQ_REASONS.HAS_PHYSICAL_INVENTORY)) {
      info.push({
        title: 'Why can’t you support physical inventory?',
        content: (
          <Text key="ccorp" as={TypographyStyles.BODY_SM}>
            At the moment, Heard supports service-based practices and doesn’t
            handle sales tax or inventory tracking for physical goods. We want
            to be upfront so you’re not missing critical support.
          </Text>
        ),
      })
    }

    if (dqReasons.includes(DQ_REASONS.HAS_MULTIPLE_OWNERS)) {
      info.push({
        title: 'Why can’t you support multiple practice owners?',
        content: (
          <Text key="multiple-owners" as={TypographyStyles.BODY_SM}>
            At the moment, Heard supports single-owner practices. Multi-owner
            structures often involve more complex tax and ownership
            considerations that we don’t currently support.
          </Text>
        ),
      })
    }

    return info
  }, [dqReasons])

  return (
    <SignupPageLayout
      maxWidth="wide"
      hideProgress
      footer={
        <SignupFooter
          continueText="Return to Site"
          onContinue={() => {
            window.location.href = 'https://www.joinheard.com'
          }}
        />
      }
    >
      <div className="flex flex-row gap-14">
        <div className="self-start flex-4 flex flex-col space-y-4 mx-4">
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/seedling.svg"
            alt="seedling"
            height={170}
            width={120}
          />

          <Text variant={TypographyStyles.DISPLAY_MD}>
            We&apos;re not the right fit yet...
          </Text>

          {descriptionText}

          <Text variant={TypographyStyles.H1} className="mt-8">
            Learn more
          </Text>
          <Accordion type="single" collapsible className="w-full">
            {learnMoreAccInfo.map((item) => (
              <Accordion.Item key={item.title} value={item.title}>
                <Accordion.Trigger>
                  <Text variant={TypographyStyles.LABEL_SM}>
                    <u>{item.title}</u>
                  </Text>
                </Accordion.Trigger>
                <Accordion.Content>{item.content}</Accordion.Content>
              </Accordion.Item>
            ))}
          </Accordion>

          <Card color={CardColors.EMPHASIS}>
            <Text>
              Think this might be a mistake? Reach out to{' '}
              <Link href="mailto:hello@joinheard.com">hello@joinheard.com</Link>
              , and we’ll review your situation. If your practice changes down
              the line, feel free to come back anytime.
            </Text>
          </Card>
        </div>
        <div className="flex-3 self-center">
          <KeepInTouchCard />
        </div>
      </div>
    </SignupPageLayout>
  )
}
